import React, { useState } from 'react';
import { GrammarStats, GrammarGraph, GrammarSettings, GrammarImport } from './components';
import { DatabaseEditor } from '../GrammarDatabase/DatabaseEditor';

type TabType = 'explorer' | 'stats' | 'graph' | 'settings' | 'import' | 'categories' | 'database';

const DEFAULT_PRACTICE_PROMPT = `Generate exactly 2 multiple choice questions to test understanding of the grammar feature: {{grammarFeature}}.

Focus on:
- Definition and recognition of "{{grammarFeature}}"
- Correct usage in business contexts
- Common errors and how to avoid them
- CEFR level: {{cefrIntroduction}} (introduction) / {{cefrMastery}} (mastery)
- Complexity score: {{complexityScore}}/10

Grammar category: {{category}}
Description: {{description}}

{{#if businessContexts}}
Include business-relevant examples from: {{businessContexts}}
{{/if}}

{{#if commonErrors}}
Address these common mistakes: {{commonErrors}}
{{/if}}

{{#if importantFootnotes}}
Important notes: {{importantFootnotes}}
{{/if}}

Each question should have 4 options with detailed explanations that help learners understand why each answer is correct or incorrect.`;

const GrammarDictionary: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('database');
  const [practicePrompt, setPracticePrompt] = useState<string>(DEFAULT_PRACTICE_PROMPT);

  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
  };

  const tabs = [
    { id: 'database', label: 'Grammar Database' },
    { id: 'stats', label: 'Grammar Stats' },
    { id: 'graph', label: 'Grammar Graph' },
    { id: 'settings', label: 'Settings' },
    { id: 'import', label: 'Import' }
  ];

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header with tabs */}
      <div className="border-b border-border bg-gray-50">
        <div className="px-6 pt-4">
          <h1 className="text-xl font-semibold text-purple-900 mb-4">Grammar Dictionary</h1>
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as TabType)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-purple-900 border-x border-t border-gray-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Error display */}
      {error && (
        <div className="mx-6 mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
        </div>
      )}
      
      {/* Content area */}
      <div className="flex-1 min-h-0">
        {activeTab === 'database' && <DatabaseEditor onError={setError} />}
        {activeTab === 'stats' && (
          <div className="p-6 overflow-auto">
            <GrammarStats onError={setError} />
          </div>
        )}
        {activeTab === 'graph' && (
          <div className="p-6 overflow-auto">
            <GrammarGraph onError={setError} />
          </div>
        )}
        {activeTab === 'settings' && (
          <div className="p-6 overflow-auto">
            <GrammarSettings onError={setError} practicePrompt={practicePrompt} setPracticePrompt={setPracticePrompt} />
          </div>
        )}
        {activeTab === 'import' && (
          <div className="p-6 overflow-auto">
            <GrammarImport onError={setError} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GrammarDictionary; 